import { useTranslation } from "react-i18next";
import { Grid, GridColumn } from "semantic-ui-react";

import { DealersAndLocationsDropdown, ExportDataButton, NavPortal, ResetFiltersButton, SearchFilter, useSelectedLocation } from "components";
import { usePermanentRealTimeUser } from "hooks";
import "modules/BackordersDashboard/components/BackordersDashboardFilters/BackordersDashboardFilters.scss";
import { setBackordersDashboardFilters, useBackordersDashboardFilters } from "modules/BackordersDashboard/components/BackordersDashboardFilters/hooks";
import { BackordersExportModal } from "modules/BackordersDashboard/components/BackordersExportModal";
import { useExportBackordersMutation } from "modules/BackordersDashboard/components/BackordersExportModal/hooks";
import { BACKORDERS_STORAGE_KEYS } from "modules/BackordersDashboard/storageKeys";
import { setPreference } from "utils/common";

import type { DealersLocationsDropdownValue } from "components";
import type { DealerLocationID } from "models";

export const BackordersDashboardFilters = () => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { selectedLocation } = useSelectedLocation();
  const filters = useBackordersDashboardFilters();
  const { mutate: queueBackordersExport, isIdle, isPending, isSuccess, isError, reset } = useExportBackordersMutation();

  const handleDealerLocationChange = ({ dealerIds, locationIds }: DealersLocationsDropdownValue) => {
    setPreference(BACKORDERS_STORAGE_KEYS.DealerPreference, dealerIds);
    setPreference(BACKORDERS_STORAGE_KEYS.LocationPreference, locationIds);
    setBackordersDashboardFilters({ dealer_ids: dealerIds, location_ids: locationIds });
  };

  const handleResetFilters = (selectedLocationId?: DealerLocationID) => {
    setPreference(BACKORDERS_STORAGE_KEYS.DealerPreference, []);
    setPreference(BACKORDERS_STORAGE_KEYS.LocationPreference, selectedLocationId ? [selectedLocationId] : []);
    setPreference(BACKORDERS_STORAGE_KEYS.AppointmentDateFromPreference, null);
    setPreference(BACKORDERS_STORAGE_KEYS.AppointmentDateToPreference, null);
    setPreference(BACKORDERS_STORAGE_KEYS.DateFromPreference, null);
    setPreference(BACKORDERS_STORAGE_KEYS.DateToPreference, null);

    setBackordersDashboardFilters({
      page: 1,
      search_term: "",
      dealer_ids: [],
      location_ids: selectedLocationId ? [selectedLocationId] : [],
      appointment_date_from: null,
      appointment_date_to: null,
      backorder_date_from: null,
      backorder_date_to: null
    });
  };

  return (
    <>
      <NavPortal>
        <SearchFilter value={filters.search_term} handleSearchChange={search_term => setBackordersDashboardFilters({ search_term })} />
      </NavPortal>
      <div className="BackordersDashboardFilters__toolbar-container">
        <Grid columns={16} className="BackordersDashboardFilters__toolbar-grid">
          <GridColumn width={7} className="BackordersDashboardFilters__no-left-gutter">
            <DealersAndLocationsDropdown
              values={{ dealerIds: filters.dealer_ids, locationIds: filters.location_ids }}
              handleOnChange={({ dealerIds, locationIds }) => handleDealerLocationChange({ dealerIds, locationIds })}
              allowSelectAllDealers={false}
            />
          </GridColumn>
        </Grid>
        <div className="BackordersDashboardFilters__toolbar-reset-export-buttons-container">
          <ResetFiltersButton handleReset={() => handleResetFilters(selectedLocation?.id)} />
          {user?.csv_download && (
            <ExportDataButton
              contentMessage={`${t("v8_export_disabled_as_no_dealer_location_selected", "export disabled as no dealer or location have been selected")}.`}
              handleOnClick={() => queueBackordersExport(filters)}
              hasWarning={filters.dealer_ids.length === 0 && filters.location_ids.length === 0}
            />
          )}
        </div>
      </div>
      <BackordersExportModal open={!isIdle} isPending={isPending} isSuccess={isSuccess} isError={isError} onClose={reset} />
    </>
  );
};
